/**
 * Interface representing a basic answer with only the answer text.
 */
interface BasicAnswer {
  the_answer: string
}

/**
 * Interface for answer statistics, includes the answer and its count.
 */
interface AnswerStat {
  the_answer: string
  count: number
}

/**
 * Interface representing a row in a matrix question, containing the question text,
 * the number of effective responses, and statistics for each answer.
 */
interface MatrixxRow {
  the_question: string
  effective_response_count: number
  answer_stats: AnswerStat[]
}

/**
 * Interface representing a percentage value with a label.
 */
interface Percentage {
  label: string
  percentage: number
}

/**
 * Interface representing a partial data structure for a matrix chart.
 */
interface MatrixxChart {
  answers: BasicAnswer[]
  answer_stats: MatrixxRow[]
}

/**
 * Interface representing a segment of the chart, containing a label and
 * a collection of percentage values.
 */
interface Segment {
  label: string
  segments: Percentage[]
}

/**
 * Calculates percentage values for each answer in a matrix chart.
 *
 * This function processes matrix chart data to convert raw counts into percentages.
 * For each question (row) in the matrix, it calculates the percentage of each
 * possible answer relative to the total number of responses.
 * NOTE: When applying filters or marking answers as `exclude_from_stats` the response
 * coming from the backend will have less rows than the total number of answers in the matrix.
 *
 * @param {MatrixxChart} chart - The matrix chart data containing answers and statistics
 * @returns {Segment[]} An array of segments, each containing percentages for a question
 */
export function calculatePercentages({ answers, answer_stats }: MatrixxChart): Segment[] {
  if (answer_stats.length === 0) return []

  return answer_stats.map((mRow) => {
    const statsMap = Object.fromEntries(
      mRow.answer_stats.map((stat) => [stat.the_answer, stat.count]),
    )

    const totalResponses = mRow.effective_response_count

    const segments = answers.map((answer) => {
      const count = statsMap[answer.the_answer]

      return {
        label: answer.the_answer,
        percentage: count && totalResponses > 0 ? Math.round((count / totalResponses) * 100) : 0,
      }
    })

    return {
      label: mRow.the_question,
      segments: segments,
    }
  })
}
